import { type ExportHistoryRow, ExportHistoryTable } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { listProjectCustomData } from '~/api/projectCustomData';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import { customDataQueryKey } from '~/pages/projectDetails/filesAndMedia/CustomDataSection';
import { layerIdToDownloadLayer, layerIdToDownloadLayerOverrides } from '~/store2/downloadLayers';
import { datasetConfig } from '~/store2/specialDatasets';
import { TOC } from '~/store2/TOCLayers';
import { CUSTOM_DATA_LAYER_PREFIX } from '../map/customDataLayers.helpers';
import ExportLayersHoverCard from './ExportLayersHoverCard';
import { downloadExportRun } from './fileDownload';
import { buildTocLayerIndex, type ResolvedExportLayer, resolveExportLayers } from './layerLabels';
import {
  type ExportHistoryLoadOutcome,
  loadExportHistory,
  reportAwaitingSubmissionTimeout,
  useReportCustomDataLoadFailure,
} from './loadTelemetry';
import { AWAITING_POLL_TIMEOUT_MS, type ExportRun, getHistoryPollInterval, toExportRuns } from './runs';
import downloadStore from './store';

type ExportHistoryProps = {
  projectId: string;
  /** Called with the first fetch's outcome after the render that shows its rows or empty state. */
  onInitialLoad?: (outcome: ExportHistoryLoadOutcome) => void;
};

/** Export history as one row per submission, with the run's whole archive behind one download. */
export default function ExportHistory({ projectId, onInitialLoad }: ExportHistoryProps) {
  const { downloads, awaitingSubmissionId } = useSnapshot(downloadStore) as typeof downloadStore;
  const labels = useExportLabels();
  const escalationsEnabled = useFeatureFlagEnabled('export-enable-escalations') === true;

  const [initialOutcome, setInitialOutcome] = useState<ExportHistoryLoadOutcome | null>(null);

  useEffect(() => {
    let active = true;
    void loadExportHistory(projectId, 'initial').then((outcome) => {
      if (active) setInitialOutcome(outcome);
    });
    return () => {
      active = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (initialOutcome) onInitialLoad?.(initialOutcome);
  }, [initialOutcome, onInitialLoad]);

  useEffect(() => {
    return () => {
      downloadStore.reset();
    };
  }, []);

  const runs = useMemo(() => toExportRuns(downloads), [downloads]);

  const pollInterval = getHistoryPollInterval({ awaitingSubmissionId, runs });

  // Fetch the moment a submission is accepted, so the first attempt isn't a whole interval away.
  useEffect(() => {
    if (!awaitingSubmissionId) return;

    void loadExportHistory(projectId, 'poll');
  }, [projectId, awaitingSubmissionId]);

  // Stop waiting after the cap; `getProjectDownloads` clears this itself once the rows land, which
  // cancels this timeout before it fires — so reaching the callback always means the rows never
  // showed up in time, not that they arrived and this just lost the race.
  useEffect(() => {
    if (!awaitingSubmissionId) return;

    const timeoutId = setTimeout(
      () => reportAwaitingSubmissionTimeout(projectId, awaitingSubmissionId),
      AWAITING_POLL_TIMEOUT_MS,
    );

    return () => clearTimeout(timeoutId);
  }, [projectId, awaitingSubmissionId]);

  useEffect(() => {
    if (pollInterval === null) return;

    const intervalId = setInterval(() => {
      void loadExportHistory(projectId, 'poll');
    }, pollInterval);

    return () => clearInterval(intervalId);
  }, [projectId, pollInterval]);
  const runsById = useMemo(() => new Map(runs.map((run) => [run.id, run])), [runs]);

  // Custom-data labels resolve off the project's rows. Fetched only when a run references one; the
  // cache is shared with the picker and files panel (customDataQueryKey), so it's usually warm.
  const hasCustomDataLayer = useMemo(
    () => runs.some((run) => run.layers.some((layer) => layer.startsWith(CUSTOM_DATA_LAYER_PREFIX))),
    [runs],
  );
  const { data: customDataRows, error: customDataError } = useQuery({
    queryKey: customDataQueryKey(projectId),
    queryFn: () => listProjectCustomData(projectId),
    enabled: hasCustomDataLayer && Boolean(projectId),
    refetchOnWindowFocus: false,
    retry: false,
  });

  useReportCustomDataLoadFailure(projectId, 'history', customDataError, hasCustomDataLayer);

  // Naming the layers is what decides the count, so the table and the card can't disagree — a run
  // stores layers the picker no longer offers, and the ESRI form stores the project outline too.
  const layersByRun = useMemo(() => {
    // Rebuilt whenever the run list changes identity, which the poll and the initial fetch both do.
    // The TOC and picker map are refilled in place without notifying, and only during bootstrap and
    // org switches — so a switch made while this panel is open leaves a resolved layer on its old
    // section until the next fetch replaces `runs`.
    const index = buildTocLayerIndex([
      { toc: TOC },
      ...Object.values(datasetConfig).map(({ name, toc }) => ({ toc, parent: name })),
    ]);
    const customDataById = Object.fromEntries((customDataRows ?? []).map((row) => [row.id, row]));
    const maps = { layerIdToDownloadLayer, overrides: layerIdToDownloadLayerOverrides, customDataById };

    return new Map<string, ResolvedExportLayer[]>(
      runs.map((run) => [run.id, resolveExportLayers(run.layers, index, maps)]),
    );
  }, [runs, customDataRows]);

  const rows: ExportHistoryRow[] = useMemo(
    () =>
      runs.map((run) => ({
        id: run.id,
        date: DateTime.fromISO(run.createdAt).toFormat('MM/dd/yyyy'),
        time: DateTime.fromISO(run.createdAt).toFormat('hh:mm a'),
        user: { name: run.user.name ?? run.user.email, email: run.user.email },
        layerCount: layersByRun.get(run.id)?.length ?? 0,
        formats: run.formats.flatMap((entry) => (entry.format ? [entry.format] : [])),
        status: run.status,
      })),
    [runs, layersByRun],
  );

  // Run ids with a signing request in flight.
  const downloading = useRef(new Set<string>());

  const renderLayerCount = (row: ExportHistoryRow, count: React.ReactNode) => {
    const run = runsById.get(row.id);

    if (!run) return count;

    return (
      <ExportLayersHoverCard
        title={labels.historyLayersPopoverHeader}
        layers={layersByRun.get(row.id) ?? []}
        projectionTitle={run.projection?.title}
        triggerTestId={`export-history-layers-trigger-${row.id}`}
        cardTestId={`export-history-layers-card-${row.id}`}
      >
        {count}
      </ExportLayersHoverCard>
    );
  };

  return (
    <div className="flex w-full flex-col gap-2" data-testid="export-history">
      <div className="text-lg font-medium">{labels.historyTab}</div>
      <ExportHistoryTable
        rows={rows}
        onDownload={(row) => {
          const run = runsById.get(row.id);

          // Signing is a round-trip, so a second click before it lands would sign and count the
          // same download twice.
          if (!run || downloading.current.has(row.id)) return;

          downloading.current.add(row.id);
          void downloadExportRun(run as ExportRun).finally(() => downloading.current.delete(row.id));
        }}
        renderLayerCount={renderLayerCount}
        emptyTitle={labels.historyEmptyTitle}
        emptyDescription={labels.historyEmptyDescription}
        userHeader={labels.historyRequestedByColumn}
        buildingLabel={escalationsEnabled ? labels.historyProcessingLabel : labels.historyBuildingLabel}
        failedLabel={labels.historyFailedLabel}
        delayedTooltip={escalationsEnabled ? labels.historyDelayedTooltip : undefined}
        delayedTooltipTestId={escalationsEnabled ? (row) => `export-history-delayed-tooltip-${row.id}` : undefined}
        noDataTooltip={escalationsEnabled ? labels.historyNoDataTooltip : undefined}
        noDataTooltipTestId={escalationsEnabled ? (row) => `export-history-no-data-tooltip-${row.id}` : undefined}
        downloadActionLabel={labels.downloadReadyHover}
        downloadTestId={(row) => `export-history-download-${row.id}`}
        layerCountTestId={(row) => `export-history-layer-count-${row.id}`}
        statusTestId={(row) => `export-history-status-${row.id}`}
      />
    </div>
  );
}
